import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import './styles/print.css';
import './env-test';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FloatingChatProvider } from './context/FloatingChatContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { preloadComponents } from './utils/forceIncludeTabs';
import { registerSW } from 'virtual:pwa-register';

// Ensure lazy-loaded components are included in the build
preloadComponents();

// Helper to check if an auto-reload should be skipped.
// We only guard the Product Entry page so an in-progress entry isn't lost.
// (Previously this also permanently blocked Template-role users from ever
// receiving updates, which meant deploys never reached them.)
const shouldSkipRefresh = (): boolean => {
  return window.location.pathname.includes('/product-entry');
};

// Protect Template users from service worker auto-refresh
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // When a new service worker takes control, some apps auto-reload
    // We prevent this for Template users
    if (shouldSkipRefresh()) {
      console.log('📱 New service worker took control, but NOT refreshing for Template user');
      return;
    }
    // For other users, the reload is controlled by onNeedRefresh callback
    console.log('🔄 New service worker took control');
  });
}

// Register PWA service worker. Updates are applied automatically (except on the
// Product Entry page, to avoid interrupting an in-progress entry) so every
// deploy reliably reaches all users on their next navigation/idle check.
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    // Don't yank the page out from under an active product entry.
    if (shouldSkipRefresh()) {
      console.log('📱 Update available; deferring reload (Product Entry in progress)');
      return;
    }
    console.log('🔄 New content available — applying update');
    updateSW(true);
  },
  onOfflineReady() {
    console.log('App ready to work offline');
  },
  onRegistered(registration) {
    console.log('SW registered: ', registration);
    if (registration && registration.update) {
      registration.update();
    }
    // Periodically check for a newer deploy so long-lived tabs update too.
    setInterval(() => {
      if (registration && registration.update) {
        registration.update();
      }
    }, 30000);
  },
  onRegisterError(error) {
    console.log('SW registration error: ', error);
    // Skip auto-reload for Template users or Product Entry page
    if (shouldSkipRefresh()) {
      console.log('📱 SW error but skipping reload for Template user');
      return;
    }
    // If SW registration fails, try to clear cache and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      }).then(() => {
        window.location.reload();
      });
    }
  },
});

// Log Firebase config
console.log('Firebase Config:', {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
});

const rootElement = document.getElementById('root')!;
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <FloatingChatProvider>
              <App />
            </FloatingChatProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
